/**
 * 画幅 → 构图法则 / 投放渠道(单一真源)。
 *
 * ═══ ★★ 注意:`rule` **确实会进 prompt**(2026-08-11 更正) ═══════════════════
 * 本文件头此前写着「两项都只给用户看,都不进 prompt」,**那句话是错的** ——
 * 下面的 `ratioCompositionPhrase()` 就是拿 `rule` 拼【画幅构图】段给 `buildRawPrompt` 用的,
 * 测试机实测提交体里逐字带着它:
 *     "rawPrompt":"…【画幅构图】1:1 方版:主体居中,约占画面七成,四周留出均匀边距"
 * 2026-07-31 那次撤出的是 **9:16 档里的版面角色词**(见下),不是把整个 `rule` 撤出 prompt;
 * 文件头当时跟着改过头了。这条注释与代码矛盾了 11 天,直接后果是
 * E-01/BUG-LI-02 那条 bug 排查时一度往「像素/档位/渲染层」上找 ——
 * 因为按注释理解,rule 根本不该影响出图。
 *
 * ★所以改这张表 = 改出图指令,不是改 UI 文案。下面那三条写法约束是硬约束。
 *
 * ═══ 2026-07-31:9:16 档的版面角色词从 rule 里撤出 ═══════════════════════════
 * 原来 `rule` 是「同时给两个读者的同一句话」:进 rawPrompt 的【画幅构图】段,也显示给用户。
 * 实测**引擎把这句话里的名词当画面文字画上去了** —— 9:16 那档写的是
 * 「上段放钩子短句,中段放主体,下段放行动引导」,出来的图上就印着「行动引导」这类词。
 *
 * ★这正是本文件原来给 `channel` 写的那条理由,只是当时只堵了一半:
 *   > 「小红书首图」这种平台名进了 prompt,引擎有概率把平台名当画面文字画进图。
 *   「钩子短句 / 行动引导 / 标题」与「小红书首图」是同一类东西 —— **描述版面角色的元词**。
 *   引擎分不清哪句是让它排版、哪句是让它写字;差别只在平台名一眼看得出是错的,
 *   而「行动引导」印在图上乍看还挺像个设计。防住一半 = 没防住。
 *
 * ★为什么是撤出而不是改写措辞:换掉几个名词只是把命中率从高调到低,
 *   下一个词照样可能被画出来 —— 这类「元指令混进内容」的账我们记过不止一次。
 *   构图指导本身的价值(不然模型自己猜)不足以抵一张印着「行动引导」的废图:
 *   构图差一点用户还能用,图上印着我们的内部术语是对客事故,而且不可逆、要重付。
 *
 * ★ 全部正面表述,不写「不要…」:否定句里的名词会被字面化反向触发(V28.3 教训)。
 *   —— 这条对 UI 文案同样成立,措辞保持原样。
 */
/**
 * ═══ 2026-09-09:留白句(copyRoom)整个机制拆除 —— 老板拍板 ═══════════════════
 * 原话:「都不要有留出空间这样的词了,各个比例你都看一下,让模型自由发挥吧」。
 * 直接诱因:测试机月饼海报(任务 5694727652352)3:4 档 50+ 字文案命中 24 字阈值,
 * 追加「下部留出空间」→ 模型留出一大块下部空白(0907 已改条件性仍嫌多)。
 *
 * ★被一并撤掉的保护是 E-01/BUG-LI-02(长正文 1:1 整段被丢,详见 git 历史 0811/0907 版):
 *   当时的修法就是这些留白句。现在按老板决定全删,**若 E-01 回归(长文案正文被丢),
 *   回滚锚点是本文件 0907 版的 copyRoom 表** —— 别重新发明措辞,那五句都是验证过的。
 */
import type { Ratio } from "./useFreeCreateForm";

export interface RatioComposition {
  /** 投放渠道 —— 只用于 UI 文案,永不进 prompt(平台名会被引擎当画面文字画上去)。 */
  channel: string;
  /**
   * 构图法则 —— **同时给两个读者**:进 rawPrompt 的【画幅构图】段,也显示给用户。
   * 改它就是改出图指令,必须守住下面 RATIO_COMPOSITION 上那三条写法约束。
   */
  rule: string;
}

/**
 * ★`rule` 只许写**几何**,不许写**版面角色**。
 *
 * 这条约束是 2026-07-31 用一张废图换来的:原来 9:16 那档写的是
 * 「上段放钩子短句,中段放主体,下段放行动引导」—— 出来的图上就印着「行动引导」四个字。
 * 「钩子短句 / 行动引导 / 标题 / 信息带」这类词在引擎眼里与「画一只猫」没有区别:
 * 都是名词,都可以画。而「主体居中」「四周留出均匀边距」没有可画的实体,
 * 只能理解成摆放方式 —— 同样传达了构图,却没有能被印上去的东西。
 *
 * 落到写法上的三条(`ratioComposition.test.ts` 逐档锁):
 *   ① 只出现方位与留白(居中 / 偏上 / 横向铺开 / 留出空间)——**不写数量配额**:
 *      「约占画面七成」这类硬配额会跟内容抢地盘,1:1 那条 bug 就是它(E-01,2026-08-11 删)
 *   ② 不出现任何「要写什么字」的角色词(标题 / 文案 / 钩子 / 行动引导 / 信息带 / 卖点)
 *   ③ 全部正面表述,不写「不要…」——否定句里的名词会被字面化反向触发(V28.3 教训)
 */
export const RATIO_COMPOSITION: Record<Ratio, RatioComposition> = {
  "1:1": {
    channel: "淘宝 / 京东主图",
    // ★2026-08-11 修(测试反馈 E-01 / BUG-LI-02):原文是
    //   「方版:主体居中,**约占画面七成,四周留出均匀边距**」——
    //   五档里**唯一没有给文字留位置**的一条,还额外把七成画面判给了主体。
    //   模型老实照做:主体做到七成、四周均匀边距 → 画面上没有能排整段正文的区域 → 正文被丢。
    //
    //   E2E 同一次请求三比例对照(任务 5031761282048,唯一变量是画幅):
    //     9:16(864×1536) 40+ 字正文一字不差,排在下方留白区 ←「上下各留出空间」
    //     16:9(1536×864) 一字不差,排在左侧             ←「元素横向铺开」
    //     1:1 (1024×1024) **整段正文直接丢弃**,只剩标题  ← 没有任何留白指令
    //   短文案时是轻症(只丢角标品牌名),长文案时整段丢光 —— 同一条规则下的连续退化。
    //
    //   曾经怀疑过是「1:1 像素最少」(1024 长边 vs 另两张 1536)。那只是相关性:
    //   三档同一个 resolutionTier「1K」、同一个 provider,像素三轮完全稳定,没有随机降档。
    //   真正的因果是这条 rule 本身。所以修 rule,不去调像素/换档位/改渲染层 —— 那几条都治不到根。
    //
    //   ★判定实验(任务 5031949239296)证明**不是像素不够**:同样 1:1 / 1024×1024、
    //   同样那段 40+ 字正文,只在用户文案里手动补一句「下方留出一条横向文案区」来抵消,
    //   整段正文就一字不差地画出来了,还多排了四个功能 chip,中文全对没有一个崩字。
    //   所以「1:1 升档 1024→2048」这条路是错的:积分翻倍,而文案再长一样会丢 ——
    //   「七成」是**比例约束**,不是绝对尺寸,放大画布它照样吃掉七成。
    //
    //   ★改法(2026-08-11 用户意见:「可以弱化他的比例,另外用户也没有说一定要留出空间吧」):
    //   **删掉数量配额**。五条规则里只有 1:1 带硬配额(七成),其余全是定性描述
    //   (「主体略偏上」「元素横向铺开」)—— 硬配额是唯一会跟内容抢地盘的东西。
    //   留白措辞照抄 4:5 档已验证过的「下方留出一条横向空间」:纯几何、无版面角色词,
    //   不会重蹈 2026-07-31「行动引导」被画进图的覆辙(见下方三条约束)。
    //
    //   ★上面挂的「留白改条件性」那一票 2026-09-07 老板拍了:不止 1:1,三档竖版全部
    //   条件性(见 copyRoom 文档)。弹框小字与短文案单子的 prompt 里不再出现留白句。
    rule: "方版:主体居中",
  },
  // ★2026-09-09:copyRoom 已随留白机制整体拆除(老板拍板,见文件头)——rule 只剩纯方位词。
  "4:5": {
    channel: "朋友圈广告",
    rule: "竖版:主体略偏上",
  },
  "3:4": {
    channel: "小红书首图",
    rule: "竖版:主体集中在画面上部",
  },
  "9:16": {
    channel: "抖音 / 视频号",
    rule: "长竖版:主体居中",
  },
  "16:9": {
    channel: "官网 / 横幅",
    rule: "横版:元素横向铺开,左右视觉重量均衡,主体落在水平中线",
  },
};

/**
 * 进 prompt 的那一句 = 「画幅 + 构图法则」。
 *
 * 非枚举画幅(i2i「跟随原图」量出来的怪比例、老草稿里下架的档位)→ null,整段不出现:
 * 编一条构图法则给它,等于把一条我们没验证过的版面规矩写进出图指令。
 */
export function ratioCompositionPhrase(ratio: string | null | undefined): string | null {
  if (!ratio) return null;
  const hit = RATIO_COMPOSITION[ratio as Ratio];
  if (!hit) return null;
  return `${ratio} ${hit.rule}`;
}


/** ⚙ 弹层 / 右列用的渠道标签(纯 UI)。 */
export function ratioChannelLabel(ratio: string | null | undefined): string | null {
  if (!ratio) return null;
  return RATIO_COMPOSITION[ratio as Ratio]?.channel ?? null;
}

/** ⚙ 弹层每档下面那行小字(纯 UI)。 */
export function ratioRuleLabel(ratio: string | null | undefined): string | null {
  if (!ratio) return null;
  return RATIO_COMPOSITION[ratio as Ratio]?.rule ?? null;
}
